import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  CustomButton,
  CustomCheckbox,
} from "../../../../v2/MUI_custom_component";
import { MODULE_PERMISSION_ENUM } from "../../../Sidebar/Enum";

export const PermissionModal = ({
  initialPermissions = [],
  onSave,
  onClose,
}) => {
  const getPermissionKeys = (permissionValue) => {
    if (Array.isArray(permissionValue)) {
      return permissionValue.filter(Boolean);
    }

    const value = permissionValue?.value ?? permissionValue;
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    return value ? [value] : [];
  };

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [initialSelectedPermissions, setInitialSelectedPermissions] = useState(
    [],
  );
  const [isFullAccess, setIsFullAccess] = useState(false);

  useEffect(() => {
    const permissions = (initialPermissions || [])
      .flatMap((permissionItem) => getPermissionKeys(permissionItem))
      .filter(Boolean);

    const uniquePermissions = [...new Set(permissions)];

    const hasFullAccess = uniquePermissions.includes(
      MODULE_PERMISSION_ENUM.ALL_MODULES,
    );
    setIsFullAccess(hasFullAccess);
    const initialSelection = hasFullAccess
      ? [MODULE_PERMISSION_ENUM.ALL_MODULES]
      : uniquePermissions.filter(
          (permission) => permission !== MODULE_PERMISSION_ENUM.ALL_MODULES,
        );

    setSelectedPermissions(initialSelection);
    setInitialSelectedPermissions(initialSelection);
  }, [initialPermissions]);

  const staffPermissionList = useSelector(
    (state) => state.EmployeeReducer?.staffPermissionList,
  );

  const handleFullAccessToggle = (event) => {
    const checked = event.target.checked;
    setIsFullAccess(checked);
    setSelectedPermissions(checked ? [MODULE_PERMISSION_ENUM.ALL_MODULES] : []);
  };

  const handleLimitedAccessToggle = (permissionValue) => {
    const permissionKeys = getPermissionKeys(permissionValue);
    if (permissionKeys.length === 0) {
      return;
    }

    setIsFullAccess(false);
    setSelectedPermissions((prev) => {
      const previousLimited = prev.filter(
        (permission) => permission !== MODULE_PERMISSION_ENUM.ALL_MODULES,
      );

      const isAlreadySelected = permissionKeys.every((permission) =>
        previousLimited.includes(permission),
      );

      if (isAlreadySelected) {
        return previousLimited.filter(
          (permission) => !permissionKeys.includes(permission),
        );
      }

      return [...new Set([...previousLimited, ...permissionKeys])];
    });
  };

  const handleSave = () => {
    const permissions = isFullAccess
      ? [MODULE_PERMISSION_ENUM.ALL_MODULES]
      : selectedPermissions.filter(
          (permission) => permission !== MODULE_PERMISSION_ENUM.ALL_MODULES,
        );
    if (onSave) {
      onSave({ permissions: permissions.flat() });
    }

    if (onClose) {
      onClose();
    }
  };

  const hasAnySelection = selectedPermissions.length > 0;
  const hasSelectionChanged = (() => {
    if (selectedPermissions.length !== initialSelectedPermissions.length) {
      return true;
    }

    const selectedSet = new Set(selectedPermissions);
    return initialSelectedPermissions.some(
      (permission) => !selectedSet.has(permission),
    );
  })();

  return (
    <Box sx={{ padding: "16px 16px 32px 16px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h2">
          Select permissions to manage & view
        </Typography>

        {!!staffPermissionList?.full_access?.length && (
          <Box>
            <Typography variant="labelD">Full access:</Typography>

            <Box
              sx={{
                paddingLeft: "4px",
                marginBottom: "8px",
                marginTop: "4px",
              }}>
              {staffPermissionList.full_access.map((perm) => (
                <Box key={perm.value} sx={{ marginBottom: "4px" }}>
                  <CustomCheckbox
                    label={perm.label}
                    checked={isFullAccess}
                    onChange={handleFullAccessToggle}
                    size="medium"
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="h2" sx={{ color: "#666", textAlign: "center" }}>
          Or
        </Typography>

        {!!staffPermissionList?.limited_access?.length && (
          <Box>
            <Typography variant="labelD">
              Limited Access:{" "}
              <Box component="span" sx={{ color: "#666" }}>
                (Select 1 or more modules)
              </Box>
            </Typography>

            <Box
              sx={{
                paddingLeft: "4px",
                marginBottom: "4px",
                marginTop: "4px",
              }}>
              {staffPermissionList?.limited_access?.map((perm) => (
                <Box key={perm.value} sx={{ marginBottom: "10px" }}>
                  {(() => {
                    const permissionKeys = getPermissionKeys(perm.value);
                    const isChecked =
                      permissionKeys.length > 0 &&
                      permissionKeys.every((permission) =>
                        selectedPermissions.includes(permission),
                      );

                    return (
                      <CustomCheckbox
                        label={perm.label}
                        checked={isChecked}
                        onChange={() => handleLimitedAccessToggle(perm.value)}
                        disabled={isFullAccess}
                        size="medium"
                      />
                    );
                  })()}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flexDirection: "column",
          gap: 1,
          display: "flex",
          padding: "0 16px 16px 16px",
        }}>
        <CustomButton
          label="Save"
          onClick={handleSave}
          disabled={!hasAnySelection || !hasSelectionChanged}
          variant="filled"
          color="primary"
          fullWidth
          sx={{ padding: "8px 16px" }}
        />
        <Typography
          variant="body1"
          sx={{ color: "#666", textAlign: "center", paddingTop: "2px" }}>
          You can modify permissions in the Staff section at any time.
        </Typography>
      </Box>
    </Box>
  );
};
